import { BridgeBase } from './bridge/bridge-base';
import { MessageNames, MessageNamespaces } from './internal-types';
import { PreapprovalDialogOptions, SocialItemsResponse, TwitterSubscriptionStatusResponse } from './external-types';
import { validateObject, validateString } from './utils';

/**
 * Social API is a part of the Dashboard SDK used to access social media related data.
 * Use global `enplug.social` object to access these methods.
 */
export class Social {
  /** @internal */
  constructor(private bridge: BridgeBase) { }

  clearQueryString(): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.ClearQueryString,
    });
  }

  authInstagram(accessToken: string): Promise<any> {
    validateString(accessToken, 'No authCode provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.AuthInstagram,
      params: {
        accessToken,
      },
    });
  }

  /* OBSOLETE. REMOVE AFTER 2020 */
  authSlack(verificationCode: string, redirectUrl: string): Promise<any> {
    validateString(verificationCode, 'No Slack auth token provided');
    validateString(redirectUrl, 'No redirect url provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.AuthSlack,
      params: {
        verificationCode,
        redirectUrl,
      },
    });
  }

  authenticateSlack(verificationCode: string, redirectUrl: string): Promise<any> {
    validateString(verificationCode, 'No Slack auth token provided');
    validateString(redirectUrl, 'No redirect url provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.AuthenticateSlack,
      params: {
        verificationCode,
        redirectUrl,
      },
    });
  }

  authenticateTeamsUser(verificationCode: string, redirectUrl: string): Promise<any> {
    validateString(verificationCode, 'No Teams User auth token provided');
    validateString(redirectUrl, 'No redirect url provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.AuthenticateTeamsUser,
      params: {
        verificationCode,
        redirectUrl,
      },
    });
  }

  /**
   * Saves Teams Admin token to backend.
   * @param tenantId - A user id returned from MS Teams after authenticating.
   */
  authenticateTeamsAdmin(tenantId: string): Promise<any> {
    validateString(tenantId, 'No Teams Admin auth token provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.AuthenticateTeamsAdmin,
      params: {
        tenantId,
      },
    });
  }

  getInstagramAccounts(userId: string): Promise<any> {
    validateString(userId, 'No Facebook User Id provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.GetInstagramAccounts,
      params: {
        userId,
      },
    });
  }

  lookupTwitterId(username: string): Promise<any> {
    validateString(username, 'No username provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.LookupTwitterId,
      params: username,
    });
  }

  twitterSubscriptionStatus(): Promise<TwitterSubscriptionStatusResponse> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.TwitterSubscriptionStatus,
    });
  }

  getSlackTeams(): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.GetSlackTeams,
    });
  }

  getSlackChannels(teamId: string): Promise<any> {
    validateString(teamId, 'No teamId provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.GetSlackChannels,
      params: teamId,
    });
  }

  /**
   * Returns Channels available for a given user ID.
   * @param tenantId - An ID returned after authenticating the user with MS Teams.
   */
  getTeamsChannels(tenantId: string): Promise<any> {
    validateString(tenantId, 'No tenantId provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.GetTeamsChannels,
      params: tenantId,
    });
  }

  addFacebookPage(feedId: string, pageId: string, accessToken: string): Promise<any> {
    validateString(feedId, 'No feedId provided');
    validateString(pageId, 'No pageId provided');
    validateString(accessToken, 'No accessToken provided');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.AddFacebookPage,
      params: {
        feedId,
        pageId,
        accessToken,
      },
    });
  }

  getFeeds(assetId: string): Promise<any> {
    validateString(assetId, 'No assetid provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.GetFeeds,
      params: assetId,
    });
  }

  saveFeed(feed: any): Promise<any> {
    validateObject(feed, 'No feed provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.SaveFeed,
      params: feed,
    });
  }

  deleteFeed(feedId: string): Promise<any> {
    validateString(feedId, 'No feedId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.DeleteFeed,
      params: feedId,
    });
  }

  openPreapprovalDialog(feed: any, iconUrl: string, options?: PreapprovalDialogOptions): Promise<any> {
    validateObject(feed, 'No feed provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.OpenPreapprovalDialog,
      params: {
        feed,
        iconUrl,
        options,
      },
    });
  }

  loadAllItems(assetId: string): Promise<SocialItemsResponse> {
    validateString(assetId, 'No assetId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.LoadAllItems,
      params: assetId,
    });
  }

  approveItem(itemId: string, assetId: string): Promise<any> {
    validateString(itemId, 'No itemId provided.');
    validateString(assetId, 'No assetId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.ApproveItem,
      params: {
        itemId,
        assetId,
      },
    });
  }

  removeItem(itemId: string, assetId: string): Promise<any> {
    validateString(itemId, 'No itemId provided.');
    validateString(assetId, 'No assetId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.RemoveItem,
      params: {
        itemId,
        assetId,
      },
    });
  }

  favoriteItem(itemId: string, assetId: string, network: string): Promise<void> {
    validateString(itemId, 'No itemId provided.');
    validateString(assetId, 'No assetId provided.');
    validateString(network, 'No network provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.FavoriteItem,
      params: {
        itemId,
        assetId,
        network,
      },
    });
  }

  unfavoriteItem(itemId: string, assetId: string, network: string): Promise<any> {
    validateString(itemId, 'No itemId provided.');
    validateString(assetId, 'No assetId provided.');
    validateString(network, 'No network provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.UnfavoriteItem,
      params: {
        itemId,
        assetId,
        network,
      },
    });
  }

  banItem(itemId: string, assetId: string): Promise<any> {
    validateString(itemId, 'No itemId provided.');
    validateString(assetId, 'No assetId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.BanItem,
      params: {
        itemId,
        assetId,
      },
    });
  }

  loadBlacklist(accountId: string, socialNetwork: string): Promise<any> {
    validateString(accountId, 'No accountId provided.');
    validateString(socialNetwork, 'No socialNetwork provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.LoadBlacklist,
      params: {
        accountId,
        socialNetwork,
      },
    });
  }

  unbanUser(username: string, socialNetwork: string, accountId: string): Promise<any> {
    validateString(username, 'No username provided.');
    validateString(socialNetwork, 'No socialNetwork provided.');
    validateString(accountId, 'No accountId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.UnbanUser,
      params: {
        username,
        socialNetwork,
        accountId,
      },
    });
  }

  /**
   * Gets new Facebook posts
   */
  refreshFeed(feedId: string): Promise<any> {
    validateString(feedId, 'No feedId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.RefreshFeed,
      params: feedId,
    });
  }

  /**
  * Gets new Twitter posts
  */
  refreshTwitterFeed(assetId: string, feedId: string): Promise<any> {
    validateString(assetId, 'No assetId provided.');
    validateString(feedId, 'No feedId provided.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Social,
      name: MessageNames.RefreshTwitterFeed,
      params: {
        assetId,
        feedId,
      },
    });
  }
}
